import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import * as PR from "../../../prime-modules/index";
import "./LanguageTranslate.scss";
import { getLanguageTranslates, uploadTranslations } from "../../../services/api";
import cloneDeep from 'lodash/cloneDeep';
import { useDispatch } from "react-redux";
import { languageConfig, pageConfig } from "../header/MenuConfig";

const TranslationRow = React.memo(({
  page,
  itemKey,
  english,
  selectedLanguage,
  errorMsg,
  isEditing,
  onInputChange,
  onEdit,
  onUpdateText,
  onRemoveErrors,
}) => {
  return (
    <div className="flex align-items-center mb-2">
      <div className="col-6 pb-0">
        <div className="box">
          <div className="flex align-items-center english-text">
            <div className="col-2 p-0">
              <span>{itemKey}</span>
            </div>
            <div className="col-10 p-0">
              <p>{english}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="col-6 pb-0">
        <div className="box">
          <div className="flex align-items-center">
            <div className="col">
              <>
                {!isEditing && <span>{selectedLanguage}</span>}

                {isEditing && (
                  <PR.InputText
                    value={selectedLanguage || ''}
                    onChange={(e) => onInputChange(page, itemKey, e.target.value)}
                    id={`title-${page}-${itemKey}`}
                    name={`title-${page}-${itemKey}`}
                  />
                )}
                <div className="priceError"> {errorMsg}</div>
              </>
            </div>
            <div className="icons">
              <>
                {!isEditing && (
                  <PR.Button
                    icon="pi pi-pencil"
                    className="action-btn edit"
                    onClick={() => onEdit(`${page}-${itemKey}`)}
                  />
                )}

                {isEditing && (
                  <>
                    <PR.Button
                      icon="pi pi-check"
                      className="action-buttons"
                      onClick={onUpdateText}
                    />

                    <PR.Button
                      icon="pi pi-times"
                      className="action-buttons close"
                      onClick={onRemoveErrors}
                    />
                  </>
                )}
              </>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const LanguageTranslate = () => {
  const appName = process.env?.REACT_APP_NAME;
  const dispatch = useDispatch();
  const toast = useRef();
  const [translatedList, setTranslatedList] = useState({});
  const [originalTranslatedList, setOriginalTranslatedList] = useState({});
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [loading, setLoading] = useState(false);
  const [editingRowKey, setEditingRowKey] = useState(null);
  const adminData = useSelector((state) => state.adminAuth.adminSessionData);
  const headers = useMemo(() => {
    return { sessionid: adminData.sessionId };
  }, [adminData.sessionId]);

  const languages = languageConfig[appName] ? languageConfig[appName] : [
    { name: "Dutch", code: "nl" },
    { name: "English", code: "en" },
    { name: "Portuguese", code: "pt" },
    { name: "Spanish", code: "es" },
    { name: "Tagalog", code: "tl" },

  ];
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);
  const [filteredSectionsList, setFilteredSectionsList] = useState([]);
  const [filteredSection, setFilteredSection] = useState('All');
  const disableSections = useMemo(() => {
    return ['affiliate', 'partner']
  }, [])
  const [searchText, setSearchText] = useState('');
  const isBase64Image = (image) => {
    if (image?.startsWith('iVBORw0KGgoAAAANSUhEUg') || image?.startsWith('/9j/4AAQSkZJRgA')) {
      return true
    }
    return false
  }

  const getTranslations = useCallback(() => {
    setLoading(true);
    const lang = selectedLanguage.code;
    getLanguageTranslates(lang, headers, dispatch, (response) => {
      setLoading(false);
      if (response.result === "SUCCESS") {
        const list = response.data ? response.data : {};
        const keysToRemove = ['faq-new', 'faqjson', 'faq', 'policyTerms', 'variables', 'studies', 'cmsData', 'settings'];
        if (appName !== 'elefone' && appName !== 'briant') {
          keysToRemove.push('general')
        }
        keysToRemove.forEach((key) => {
          if (list?.[key]) {
            delete list[key];
          }
        });
        setTranslatedList(list);
        const data2goIotFilter = ['labelsIot', 'validationsIot'];
        let dropdownOptions = ['All', ...Object.keys(list)].filter(f => !data2goIotFilter.includes(f))
        if (appName === "esimcrew") {
          dropdownOptions = dropdownOptions?.filter(s => !disableSections?.includes(s));
        } else if (appName === 'data2go-iot') {
          dropdownOptions = ['labelsIot', 'messageCatalog', 'validationsIot', "landingPage"];;
          setFilteredSection('labelsIot');
        } else if (appName === 'elefone' || appName === 'briant') {
          dropdownOptions = ['All', 'auth', 'config', 'domesticSim', 'esim', 'family', 'general', 'market', 'payment', 'plan', 'profile', 'redeemVoucher', 'support', 'messageCatalog', 'validations', "voucher"];
          setFilteredSection('All');
        }
        dropdownOptions = dropdownOptions.map(id => ({ id: id, name: pageConfig[id] ?? id })).sort((a, b) => a.name.localeCompare(b.name))
        setFilteredSectionsList(dropdownOptions)
        setOriginalTranslatedList(cloneDeep(list));
      } else {
        const error = response.error;
        toast.current.show({
          severity: error.severity,
          summary: "Error",
          detail: error.errorMsg || error.summary,
        });
      }
    });
  }, [appName, disableSections, dispatch, headers, selectedLanguage.code]);

  const handleInputChange = useCallback((page, key, value) => {
    setPendingUpdates((prevUpdates) => ({
      ...prevUpdates,
      [page]: {
        ...prevUpdates[page],
        [key]: value,
      },
    }));

    setTranslatedList((prevList) => ({
      ...prevList,
      [page]: {
        ...prevList[page],
        [key]: {
          ...prevList[page]?.[key],
          selectedLanguage: value,
        },
      },
    }));
  }, []);

  const handleEditClick = useCallback((rowKey) => {
    setEditingRowKey(rowKey);
  }, []);

  const updateText = useCallback(() => {
    setLoading(true)
    if (Object.keys(pendingUpdates).length > 0) {
      uploadTranslations(selectedLanguage.code, pendingUpdates, headers, dispatch, (response) => {
        setLoading(false)
        if (response.result === "SUCCESS") {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Translations updated successfully",
          });
          setPendingUpdates({});
          setOriginalTranslatedList(cloneDeep(translatedList))
        } else {
          const error = response.error;
          toast.current.show({
            severity: error.severity,
            summary: "Error",
            detail: error.errorMsg || error.summary,
          });
        }
      });
    } else {
      toast.current.show({
        severity: "info",
        summary: "No Changes",
        detail: "No translations were changed.",
      });
    }
    setEditingRowKey(null);
  }, [dispatch, headers, pendingUpdates, selectedLanguage.code, translatedList]);

  const removeTextErrors = useCallback(() => {
    setTranslatedList(cloneDeep(originalTranslatedList));
    setPendingUpdates({});
    setEditingRowKey(null);
  }, [originalTranslatedList]);

  useEffect(() => {
    getTranslations();
  }, [getTranslations]);

  const languageList = useMemo(() => {
    const isExistFn = text => text.toLowerCase()?.includes(searchText?.toLowerCase())
    const filteredTranslations = !!translatedList[filteredSection] ? { [filteredSection]: translatedList[filteredSection] } : translatedList;
    return Object.entries(filteredTranslations).flatMap(
      ([page, entry]) => Object.entries(entry).map(([key, item]) => ({
        page: page,
        key: key,
        english: item.english,
        selectedLanguage: item.selectedLanguage,
        errorMsg: item.errorMsg || "",
      }))
        .filter(s => !s.key.includes('.'))
        .filter(s => !isBase64Image(s.english))
        .filter(s => isExistFn(s.key) || isExistFn(s.english))
    );
  }, [translatedList, filteredSection, searchText]);

  return (
    <div className="language-translate">
      <div className="grid mb-2">
        <div className="col-12">
          <div className="flex align-items-center justify-content-between">
            <h2>English</h2>
            <div className="dropdown flex align-items-center gap-2">
              <div className="relative">
                <PR.InputText value={searchText} onChange={e => setSearchText(e.target.value)} className="language-search-field" placeholder="Search.." />
                <span className="pi pi-search"></span>
              </div>
              <PR.Dropdown
                options={filteredSectionsList}
                value={filteredSection}
                optionValue="id"
                optionLabel="name"
                onChange={e => setFilteredSection(e.value)}
                placeholder="Filter Screens"
              />
              <PR.Dropdown
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.value)}
                options={languages}
                optionLabel="name"
                placeholder="Select a language"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="custom-bg">
        {loading ? (
          <div className="col-12 text-center">
            <PR.ProgressSpinner />
          </div>
        ) : (
          languageList.map((lang) => (
            <TranslationRow
              key={`${lang.page}-${lang.key}`}
              page={lang.page}
              itemKey={lang.key}
              english={lang.english}
              selectedLanguage={lang.selectedLanguage}
              errorMsg={lang.errorMsg}
              isEditing={editingRowKey === `${lang.page}-${lang.key}`}
              onInputChange={handleInputChange}
              onEdit={handleEditClick}
              onUpdateText={updateText}
              onRemoveErrors={removeTextErrors}
            />
          ))
        )}
      </div>
      <PR.Toast ref={toast} position="top-right" />
    </div>
  );
};

export default LanguageTranslate;
